import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Heading3, Undo2, Redo2, Link2, Pilcrow,
} from "lucide-react";

// ── The PRD rich text editor ─────────────────────────────────────────────────
//
// Separate from the shared RichTextEditor for two reasons, both of which bite
// in exactly this place:
//
//   Every control is type="button". The project wizard is a <form> and the PRD
//   editor renders inside it, so a toolbar button without an explicit type is
//   a submit button — one click on Bold would create the project.
//
//   Content is pushed in when it changes underneath. Regeneration replaces the
//   document while the editor is open, and TipTap's `content` option is only
//   read on mount, so without this the editor would keep showing the old draft.

interface Props {
  value: string;
  onChange: (html: string) => void;
  editable?: boolean;
}

const Tool = ({
  onClick, active, title, disabled, children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    aria-pressed={active}
    disabled={disabled}
    className={`h-8 min-w-8 px-1.5 inline-flex items-center justify-center rounded-md text-[12px] font-semibold transition-colors disabled:opacity-40 ${
      active
        ? "bg-primary text-white"
        : "text-on-surface-variant hover:bg-surface-container hover:text-on-surface"
    }`}
  >
    {children}
  </button>
);

const PrdEditor = ({ value, onChange, editable = true }: Props) => {
  // True only while we are pushing content in from outside. `emitUpdate: false`
  // already suppresses the update event, but this does not depend on that
  // holding: if an onChange ever escaped a programmatic setContent, the modal
  // would mark the document dirty immediately after every save, and the person
  // would be told they had unsaved changes they never made.
  const applying = useRef(false);

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: false }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[320px] px-4 py-3 " +
          "prose-headings:font-bold prose-h2:text-[15px] prose-h2:mt-5 prose-h2:mb-1.5 " +
          "prose-h3:text-[13px] prose-p:text-[13px] prose-li:text-[13px] prose-p:my-1.5",
      },
    },
    onUpdate: ({ editor }) => {
      if (applying.current) return;
      onChange(editor.getHTML());
    },
  });

  // Replace the document when it changes from outside (generate, regenerate,
  // discard). Guarded on the current HTML so typing never fights the effect.
  useEffect(() => {
    if (!editor) return;
    if (value === editor.getHTML()) return;
    applying.current = true;
    try {
      editor.commands.setContent(value || "", { emitUpdate: false });
    } finally {
      // setContent is synchronous, so the flag covers exactly this write.
      applying.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  if (!editor) return null;

  const chain = () => editor.chain().focus();

  return (
    <div className="border border-outline-variant rounded-xl bg-surface-container-lowest overflow-hidden">
      {editable && (
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-outline-variant bg-surface-container-low sticky top-0 z-10">
          <Tool onClick={() => chain().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
            <Bold size={14} />
          </Tool>
          <Tool onClick={() => chain().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
            <Italic size={14} />
          </Tool>
          <Tool onClick={() => chain().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline">
            <UnderlineIcon size={14} />
          </Tool>

          <span className="w-px h-5 bg-outline-variant mx-1" />

          <Tool onClick={() => chain().setParagraph().run()} active={editor.isActive("paragraph")} title="Body text">
            <Pilcrow size={14} />
          </Tool>
          {/* H2 is the section heading. Renaming one changes the document's
              structure, which validation reports rather than prevents. */}
          <Tool
            onClick={() => chain().toggleHeading({ level: 2 }).run()}
            active={editor.isActive("heading", { level: 2 })}
            title="Section heading"
          >
            <Heading2 size={14} />
          </Tool>
          <Tool
            onClick={() => chain().toggleHeading({ level: 3 }).run()}
            active={editor.isActive("heading", { level: 3 })}
            title="Sub-heading"
          >
            <Heading3 size={14} />
          </Tool>

          <span className="w-px h-5 bg-outline-variant mx-1" />

          <Tool onClick={() => chain().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list">
            <List size={14} />
          </Tool>
          <Tool onClick={() => chain().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list">
            <ListOrdered size={14} />
          </Tool>

          <span className="w-px h-5 bg-outline-variant mx-1" />

          <Tool
            onClick={() => {
              const existing = editor.getAttributes("link").href || "";
              const url = window.prompt("Link URL", existing);
              if (url === null) return;
              if (!url.trim()) { chain().unsetLink().run(); return; }
              chain().setLink({ href: url.trim() }).run();
            }}
            active={editor.isActive("link")}
            title="Link"
          >
            <Link2 size={14} />
          </Tool>

          <span className="flex-1" />

          <Tool onClick={() => chain().undo().run()} disabled={!editor.can().undo()} title="Undo">
            <Undo2 size={14} />
          </Tool>
          <Tool onClick={() => chain().redo().run()} disabled={!editor.can().redo()} title="Redo">
            <Redo2 size={14} />
          </Tool>
        </div>
      )}

      <EditorContent editor={editor} />
    </div>
  );
};

export default PrdEditor;
